import React, { useEffect, useState } from 'react';
import { Cookie, X, ShieldCheck, BarChart3, Target } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';

const STORAGE_KEY = 'gt_cookie_consent_v1';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch (_) {
      setVisible(true);
    }
    const openHandler = () => {
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener('open-cookie-preferences', openHandler);
    return () => window.removeEventListener('open-cookie-preferences', openHandler);
  }, []);

  const save = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, ts: Date.now() }));
    } catch (_) { /* ignore */ }
    setVisible(false);
    setShowDetails(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => save(prefs);

  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-300" onClick={rejectAll} />
      <div className="fixed inset-x-4 bottom-4 lg:inset-x-auto lg:left-6 lg:right-6 lg:bottom-6 z-[61] mx-auto max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
        <div className="p-6 lg:p-8">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg lg:text-xl font-bold text-slate-900">Informativa sui cookie</h3>
                <button onClick={rejectAll} aria-label="Chiudi" className="text-slate-400 hover:text-slate-700 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="mt-2 text-sm lg:text-base text-slate-600 leading-relaxed">
                Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo tuo consenso, cookie analitici e di profilazione per migliorare la tua esperienza. Puoi accettare tutti i cookie, rifiutarli o personalizzare le tue preferenze.{' '}
                <a href="#privacy" className="text-sky-600 hover:text-sky-700 font-medium underline underline-offset-2">Privacy Policy</a>
                {' · '}
                <a href="#cookie" className="text-sky-600 hover:text-sky-700 font-medium underline underline-offset-2">Cookie Policy</a>
              </p>

              {showDetails && (
                <div className="mt-5 space-y-3 border-t border-slate-200 pt-5">
                  <CookieRow
                    icon={ShieldCheck}
                    title="Cookie necessari"
                    description="Indispensabili per il funzionamento del sito (sessione, preferenze, sicurezza). Non possono essere disattivati."
                    checked={true}
                    disabled
                    onChange={() => {}}
                  />
                  <CookieRow
                    icon={BarChart3}
                    title="Cookie analitici"
                    description="Ci aiutano a capire come i visitatori usano il sito in forma anonima per migliorarlo."
                    checked={prefs.analytics}
                    onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                  />
                  <CookieRow
                    icon={Target}
                    title="Cookie di marketing"
                    description="Utilizzati per mostrarti annunci rilevanti su altri siti web."
                    checked={prefs.marketing}
                    onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                  />
                </div>
              )}

              <div className="mt-5 flex flex-col sm:flex-row flex-wrap gap-2">
                {!showDetails ? (
                  <>
                    <Button onClick={acceptAll} className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 h-11 font-semibold order-1 sm:order-none">
                      Accetta tutti
                    </Button>
                    <Button onClick={rejectAll} variant="outline" className="rounded-full px-6 h-11 border-slate-300 text-slate-700 hover:bg-slate-100">
                      Rifiuta
                    </Button>
                    <Button onClick={() => setShowDetails(true)} variant="ghost" className="rounded-full px-6 h-11 text-slate-700 hover:bg-slate-100">
                      Personalizza
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={savePrefs} className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 h-11 font-semibold">
                      Salva preferenze
                    </Button>
                    <Button onClick={acceptAll} variant="outline" className="rounded-full px-6 h-11 border-slate-300 text-slate-700 hover:bg-slate-100">
                      Accetta tutti
                    </Button>
                    <Button onClick={rejectAll} variant="ghost" className="rounded-full px-6 h-11 text-slate-700 hover:bg-slate-100">
                      Rifiuta tutti
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function CookieRow({ icon: Icon, title, description, checked, onChange, disabled }) {
  return (
    <div className="flex items-start gap-4 py-3">
      <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <div className="font-semibold text-slate-900 text-sm">{title}</div>
          <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
        </div>
        <p className="mt-1 text-xs text-slate-600 leading-relaxed pr-10">{description}</p>
      </div>
    </div>
  );
}
